import { CSSObject } from "@emotion/react";
import {
  constants,
  mediaQueryStyles,
  typographyStyles,
  type AngelouCSS,
  type FilterChipStyleOverrides,
} from "@newsela/angelou";
import {
  gradientBackgrounds,
  partialCtaDefaultLinearGradientBackground,
} from "@/components/AIComponents/AiComponents.style";

const { mediaQuery: mq } = mediaQueryStyles;
const { breakpoints } = constants;
const { md } = breakpoints.BreakpointNames;

// Root container for the panel area
export const panelRootCss: CSSObject = {
  maxHeight: "fit-content",
  background: constants.colors.ui.white[500],
  padding: "16px",
  borderBottomRightRadius: "24px",

  [mq.downTo(md)]: {
    minWidth: "290px",
    maxWidth: "290px",
  },
};

// Card-like container with gradient border
export const panelCardCss = (isLoading: boolean): CSSObject => ({
  background: gradientBackgrounds[100],
  padding: isLoading ? "0" : "16px",
  borderRadius: "8px",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    padding: "1px",
    background: gradientBackgrounds[500],
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    pointerEvents: "none",
    zIndex: 0,
  },
});

// Centered overlay for the loading spinner, positioned within the panel card
export const spinnerOverlayCss: CSSObject = {
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 2,
  pointerEvents: "none",
  transition: "opacity 240ms ease-in-out, visibility 240ms ease-in-out",
};

export const spinnerHiddenCss: CSSObject = {
  opacity: 0,
  visibility: "hidden",
};

export const spinnerVisibleCss: CSSObject = {
  opacity: 1,
  visibility: "visible",
};

export const badgesRowCss: CSSObject = {
  display: "flex",
  gap: "8px",
  marginBottom: "8px",
};

export const toggleWrapperCss: CSSObject = {
  marginBottom: "16px",
};

export const sectionTitleCss: CSSObject = {
  ...typographyStyles.heading5Obj,
  fontWeight: 700,
  margin: "4px 0 8px 0",
  color: constants.colors.ui.neutral[700],
};

export const sectionDescriptionCss: CSSObject = {
  margin: "0",
  color: constants.colors.ui.neutral[700],
};

export const subsectionTitleCss: CSSObject = {
  ...typographyStyles.heading5Obj,
  fontWeight: 700,
  margin: "16px 0 4px 0",
  color: constants.colors.ui.neutral[700],
};

export const chipsGroupCss: FilterChipStyleOverrides<AngelouCSS> = {
  root: {
    display: "flex",
    flexWrap: "wrap",
    "> div": {
      marginLeft: "3px",
    },
  },
};

export const labelPromptCss = {
  ...typographyStyles.textM,
  display: "inline-block",
  fontSize: "1rem",
  lineHeight: "1.5rem",
  color: constants.colors.ui.neutral[700],
  fontWeight: 400,
  width: "80%",
};

export const chipItemWrapperCss: CSSObject = {
  marginLeft: "3px",
  li: {
    "> div": {
      maxHeight: "24px",
    },
  },
};

export const promptTitleCss: CSSObject = {
  ...typographyStyles.heading5Obj,
  fontWeight: 700,
  margin: "16px 0 4px 0",
  color: constants.colors.ui.neutral[700],
};

export const promptSectionCss: CSSObject = {
  position: "relative",
};

export const illustrationAffixTopRightCss: CSSObject = {
  position: "absolute",
  right: "-24px",
  top: "-5px",
  svg: {
    height: "105px",
    width: "75px",
  },
};

export const illustrationAffixBottomRightCss: CSSObject = {
  display: "flex",
  justifyContent: "end",
  marginTop: "8px",
};

export const emptyStateContainerCss: CSSObject = {
  maxWidth: "366px",
  overflow: "auto",
};

export const primaryActionButtonCss: CSSObject = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  maxHeight: "32px",
};

export const textareaContainerCss: CSSObject = {
  display: "flex",
  width: "100%",
};

export const textAreaOverridesCss: CSSObject = {
  height: "72px",
  width: "100%",
  overflow: "hidden",
  resize: "none",
  border: `1px solid ${constants.colors.ui.neutral[500]}`,
  borderRadius: "8px",
  color: constants.colors.ui.neutral[700],
  fontFamily: "Circular, Helvetica, Arial, sans-serif",
  fontSize: "1rem",
  padding: "12px 16px",
  lineHeight: "150%",
  margin: "8px 0",
};

export const loadingBoxCss: CSSObject = {
  height: "488px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: gradientBackgrounds[100],
  borderRadius: "12px",
};

export const refinementAreaRootCss: CSSObject = {
  width: "100%",
  height: "200px",
  display: "flex",
  alignItems: "center",
};

export const refinementLoadingBoxCss: CSSObject = {
  width: "100%",
  height: "46px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  background: "transparent",
  transition: "opacity 240ms ease-in-out, visibility 240ms ease-in-out",
};

export const refinementLoadingHiddenCss: CSSObject = {
  opacity: 0,
  visibility: "hidden",
};

export const refinementLoadingVisibleCss: CSSObject = {
  opacity: 1,
  visibility: "visible",
};

export const visibilityPermissionCss = (
  _isContentSettingsEnabled: boolean,
) => ({
  backgroundColor: "inherit",
  padding: "16px 0px",
  width: "100%",
});

export const lunaFullTagCss = {
  display: "flex",
  alignItems: "center",
  padding: "2px",
  borderRadius: "0.15rem",
  height: "20px",
  zIndex: 1,
  width: "fit-content",
  lineHeight: "100%",
  ...partialCtaDefaultLinearGradientBackground,
};
